import GameModel from "../../GameModel";
import TileMinedLogic from "../../logic/TileMinedLogic";
import { Settings } from "../../Settings";
import Crew from "../crew/Crew";
import Totals from "../crew/Totals";
import VectorField from "../grid/VectorField";
import WorldGrid from "../grid/WorldGrid";
import Vector from "../math/Vector";
import GervaisAnimatedSpritesheet from "../sprite/GervaisAnimatedSpritesheet";
import GameTime from "../time/GameTime";
import Values from "../value/Values";
import MinedUpgradeAutomationSkill from "./automation/MinedUpgradeAutomationSkill";
import PickUpgradeAutomationSkill from "./automation/PickUpgradeAutomationSkill";
import PrestigeAutomationSkill from "./automation/PrestigeAutomationSkill";
import DepthBonusSkill from "./bonus/DepthBonusSkill";
import OfflineBonusSkill from "./bonus/OfflineBonusSkill";
import OreBonusSkill from "./bonus/OreBonusSkill";
import PrestigeBonusSkill from "./bonus/PrestigeBonusSkill";
import MinedDamageBonusSkill from "./bonus/MinedDamageBonusSkill";
import DrillSkill from "./drill/DrillSkill";
import LaserSkill from "./laser/LaserSkill";
import MissileSkill from "./missile/MissileSkill";
import OrbitSkill from "./orbit/OrbitSkill";
import SkillGroup from "./SkillGroup";
import SkillType from "./SkillType";
import FallSpeedBonusSkill from "./bonus/FallSpeedBonusSkill";
import RubyBonusSkill from "./bonus/RubyBonusSkill";
import BlocksBonusSkill from "./bonus/BlocksBonusSkill";

export default class SkillTreeGraph extends GameModel {
    /**
     * @param {GameTime} gameTime
     * @param {WorldGrid} worldGrid 
     * @param {Totals} totals
     * @param {Values} values
     * @param {TileMinedLogic} tileMinedLogic 
     * @param {MineDamageLogic} mineDamageLogic
     * @param {Crew} crew
     * @param {VectorField} vectorField
     * @param {GervaisAnimatedSpritesheet} spellFxSpritesheet
     */
    constructor(gameTime, worldGrid, totals, values, tileMinedLogic, mineDamageLogic, crew, vectorField, spellFxSpritesheet) {
        super();

        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;
        /**
         * @constant
         * @type {SkillType[]}
         */
        this.skills = [];

        /**
         * @constant
         * @type {Object}
         */
        this.skillById = {};

        /**
         * @constant
         * @type {SkillGroup[]}
         */
        this.skillGroups = [];

        /**
         * This is used to determine if we need to rebuild the skill groups when a new miner
         * is added to the crew.
         * @type {number}
         */
        this.skillGroupMinerCount = -1;

        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;

        /**
         * @constant
         * @type {Vector}
         */
        this.centerOffset = new Vector(0, 0);

        /**
         * @type {number}
         */
        this.zoom = 100;

        /////////////////////////////////////
        // Add the skills
        /////////////////////////////////////

        this.addSkill(new MissileSkill(worldGrid, totals, tileMinedLogic, crew, vectorField, values, spellFxSpritesheet));

        this.addSkill(new LaserSkill(worldGrid, totals, tileMinedLogic, crew, gameTime, spellFxSpritesheet, vectorField));

        this.addSkill(new DrillSkill(worldGrid, totals, tileMinedLogic, crew, spellFxSpritesheet));

        this.addSkill(new OrbitSkill(worldGrid, totals, tileMinedLogic, crew, spellFxSpritesheet));

        this.addSkill(new OreBonusSkill(totals, values));

        this.addSkill(new PrestigeBonusSkill(totals, values, mineDamageLogic));

        this.addSkill(new DepthBonusSkill(totals, values, mineDamageLogic));

        this.addSkill(new RubyBonusSkill(totals, values, mineDamageLogic));

        this.addSkill(new BlocksBonusSkill(totals, values, mineDamageLogic));

        this.addSkill(new OfflineBonusSkill(totals, values));

        this.addSkill(new FallSpeedBonusSkill(totals, values));

        ///////this.addSkill(new UpgradeBonusAdditiveSkill(totals, values));

        this.addSkill(new MinedDamageBonusSkill(totals, values));

        this.addSkill(new MinedUpgradeAutomationSkill(totals, values));

        this.addSkill(new PickUpgradeAutomationSkill(totals, values));

        this.addSkill(new PrestigeAutomationSkill(totals, values));
    }

    /**
     * @param {number} deltaX 
     * @param {number} deltaY 
     */
    updateCenter(deltaX, deltaY) {

        // Panning is affected by the zoom level.

        const zoomFactor = this.zoom / 100.0;
        const dx = deltaX / zoomFactor;
        const dy = deltaY / zoomFactor;

        this.centerOffset.addXY(dx, dy);
    }

    /**
     * @private
     * @param {SkillType} skill 
     */
    addSkill(skill) {
        this.skills.push(skill);
        this.skillById[skill.id] = skill;
        // Do not populate the skill groups here.
    }
    
    /**
     * @param {string} id 
     * @returns {SkillType}
     */
    findSkillById(id) {
        return this.skillById[id];
    }

    /**
     * @private
     * @param {string} skillId 
     * @returns {SkillGroup}
     */
    findSkillGroupById(skillId) {
        for (let i = 0; i < this.skillGroups.length; i++) {
            const skillGroup = this.skillGroups[i];
            if (skillId === skillGroup.skillType.id) {
                return skillGroup;
            }
        }

        return null;
    }

    /**
     * Rebuilds the skill groups.  This is invoked whenever a new miner is added to the party.
     */
    rebuildSkillGroups() {
        // Clear out the skill groups.
        this.skillGroups.length = 0;

        const miners = this.crew.miners;

        let groupOffset = 0;

        for (let i = 0; i < miners.length; i++) {
            const miner = miners[i];
            const skillId = miner.skillId;

            // The 1st miner doesn't have a skill
            if (!skillId) {
                continue;
            }

            let skillGroup = this.findSkillGroupById(skillId);
            if (!skillGroup) {
                const skillType = this.findSkillById(skillId);
                if (!skillType) {
                    console.log('SkillTreeGraph.rebuildSkillGroups() Failed to find skill: ' + skillId);
                    continue    
                }

                // Create the group and add it to the list.
                skillGroup = new SkillGroup(this.gameTime, skillType, groupOffset);
                this.skillGroups.push(skillGroup);

                groupOffset += Settings.time.turnsPerSecond * 5;
            }

            // Add the miner to the group.
            skillGroup.addMiner(miner);
        }
    }

    updateSkillsForTurn() {
        this.assertMinerCountMatchesSkillGroups();

        const turnNumber = this.gameTime.turnNumber;

        for (let i = 0; i < this.skillGroups.length; i++) {
            this.skillGroups[i].updateSkillsForTurn(turnNumber);
        }
    }

    /**
     * @param {number} frameTimeRatio 
     */
    updateSkillsForFrame(frameTimeRatio) {
        this.assertMinerCountMatchesSkillGroups();

        for (let i = 0; i < this.skillGroups.length; i++) {
            this.skillGroups[i].updateSkillsForFrame(frameTimeRatio);
        }
    }

    /**
     * @private
     */
    assertMinerCountMatchesSkillGroups() {
        const miners = this.crew.miners;

        // See if we need to rebuild the skill groups.
        // This only happens when we add a miner to the crew
        if (this.skillGroupMinerCount != miners.length) {
            this.skillGroupMinerCount = miners.length
            this.rebuildSkillGroups();
        }
    }

    /**
     * @param {number} zoom 
     */
    setZoom(zoom) {
        this.zoom = zoom;
        if (this.zoom < Settings.skillGraph.minZoom) {
            this.zoom = Settings.skillGraph.minZoom;
        }
        if (this.zoom > Settings.skillGraph.maxZoom) {
            this.zoom = Settings.skillGraph.maxZoom;
        }
    }

    zoomOut() {
        this.zoom -= Settings.skillGraph.zoomStep;
        if (this.zoom < Settings.skillGraph.minZoom) {
            this.zoom = Settings.skillGraph.minZoom;
        }
    }

    zoomIn() {
        this.zoom += Settings.skillGraph.zoomStep;
        if (this.zoom > Settings.skillGraph.maxZoom) {
            this.zoom = Settings.skillGraph.maxZoom;
        }
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        for (let i = 0; i < this.skills.length; i++) {
            this.skills[i].resetFull();
        }

        // Clear out the skill groups. They are lazily created.
        this.skillGroups.length = 0;
    }

    /**
     * @override
     */
    resetForPrestige() {     
        // We don't clear our skill tree during a prestige.

        // We need to reset the start turns of the skill activation timers
        for (let i = 0; i < this.skillGroups.length; i++) {
            this.skillGroups[i].resetForPrestige();
        }
    }
}